import React from 'react';
import { SettingsBreadcrumbs } from "../settingsModule";
import { Breadcrumb } from "../../shared/web/layout/Breadcrumbs";
import InnerBlock from "../../shared/web/layout/InnerBlock";
import TabSelector from "../../shared/widgets/TabSelector/TabSelector";
import { useParams } from "react-router-dom";
import useSettingsNavigator from "../settingsNavigator";
import CustomDomainWidget from "./CustomDomainWidget";
import { useAuthorizationChecker } from "authorization-scope";
import { EmailProvider, TeamPermission, useEmailConnectionsService, EMAIL_CONNECTIONS_UPDATED } from "nekst-api";
import useFeatureToggleService from "feature-toggle";
import { useDataLoaderNew } from "data-loader";
import { LoadingAnimation, Text, TextLink } from "ui-builder";
import GmailProviderCard from "./GmailProviderCard";
import OutlookProviderCard from "./OutlookProviderCard";

import styles from './EmailWidget.module.scss';

export enum EmailSetupPage {
  PROVIDERS = 'providers',
  CUSTOM = 'custom',
}

function useEmailPage() {
  const { type } = useParams();

  if (type === EmailSetupPage.CUSTOM) {
    return EmailSetupPage.CUSTOM;
  } else {
    return EmailSetupPage.PROVIDERS;
  }
}

function EmailSetupTabs() {
  const page = useEmailPage();
  const settingsNavigator = useSettingsNavigator();
  const { isGranted } = useAuthorizationChecker();

  return (
    <TabSelector
      activeOption={page}
      onSelected={(value) => {
        settingsNavigator.openEmailSetupPage(value);
      }}
      isDisabled={!isGranted(TeamPermission.FEATURE_EMAIL_CONFIGURATION)}
      options={[
        {
          value: EmailSetupPage.PROVIDERS,
          label: 'Email Providers',
        },
        ...isGranted(TeamPermission.FEATURE_EMAIL_CONFIGURATION) ? [{
          value: EmailSetupPage.CUSTOM,
          label: 'Custom Domain',
        }] : [],
      ]}
    />
  );
}

function PolicyLinks() {
  return (
    <div className={styles.policyLinks}>
      <Text className={styles.text}>
        Refer to the 'Google Integrations (Calendar & Gmail)' and 'Microsoft Integrations (Outlook)' sections in our
        {' '}
        <TextLink onClick={() => {
          window.open('https://nekst.com/privacy', '_blank');
        }}>Privacy Policy</TextLink>
        {' '}
        to understand how your data is handled and protected.
      </Text>
    </div>
  );
}

function EmailProvidersContent() {
  const emailConnectionsService = useEmailConnectionsService();
  const { isOutlookIntegrationEnabled } = useFeatureToggleService();

  const {
    data: connection,
    loading,
    refreshData,
  } = useDataLoaderNew(
    () => emailConnectionsService.getConnection(),
    {
      dependencies: [],
      events: [EMAIL_CONNECTIONS_UPDATED]
    }
  );

  const isGmailLocked = connection?.emailProvider === EmailProvider.OUTLOOK;
  const isOutlookLocked = connection?.emailProvider === EmailProvider.GMAIL;

  if (loading) {
    return <LoadingAnimation />;
  } else {
    return (
      <>
        <GmailProviderCard
          connection={connection?.emailProvider === EmailProvider.GMAIL ? connection : null}
          isLocked={isGmailLocked}
          onConnectionChange={refreshData}
        />
        {isOutlookIntegrationEnabled() && (
          <OutlookProviderCard
            connection={connection?.emailProvider === EmailProvider.OUTLOOK ? connection : null}
            isLocked={isOutlookLocked}
            onConnectionChange={refreshData}
          />
        )}
        <PolicyLinks />
      </>
    );
  }
}

export default function UnifiedEmailPage() {
  const page = useEmailPage();

  return (
    <>
      <SettingsBreadcrumbs>
        <Breadcrumb text="Email Setup" />
      </SettingsBreadcrumbs>
      <InnerBlock title="Email Setup">
        <EmailSetupTabs />
        {page === EmailSetupPage.PROVIDERS && <EmailProvidersContent />}
        {page === EmailSetupPage.CUSTOM && <CustomDomainWidget />}
      </InnerBlock>
    </>
  );
}
